import { ModelPageConfig } from '../types';

const config: ModelPageConfig = {
  slug: 'sora-2-pro',
  modelName: 'Sora 2 Pro',
  hero: {
    title: 'Sora 2 Pro AI Video Generator',
    description:
      'Create stunning, high-quality videos with Sora 2 Pro on FalcoCut. Transform your text prompts and images into cinematic AI-generated videos with unmatched realism and creativity.',
    videos: [
      'https://assets.falcocut.ai/models/sora/skincare-influencer-video-demo.mp4',
      'https://assets.falcocut.ai/models/sora/music-gear-unboxing-video-demo.mp4',
      'https://assets.falcocut.ai/models/sora/fashion-brand-promo-video-demo.mp4',
    ],
  },
  features: {
    sectionTitle: 'Why Creators Choose Sora 2 Pro',
    items: [
      {
        title: 'Physical World Simulation',
        description:
          'Sora 2 Pro is built on advanced world simulation models that understand physics, motion, and spatial relationships. It generates videos with realistic movement, natural lighting, and believable environments, making scenes feel closer to real-world footage.',
      },
      {
        title: 'Long-Form Cinematic Generation',
        description:
          'Create longer, more complex scenes with consistent characters, environments, and camera motion. Sora 2 Pro understands cinematic language such as camera angles, shot transitions, and scene composition, enabling multi-shot storytelling directly from text prompts.',
      },
      {
        title: 'Native Video Intelligence',
        description:
          'Sora 2 Pro is trained directly on video data, allowing it to understand motion, timing, and visual continuity. This enables smoother animation, more accurate object behavior, and better scene coherence across frames.',
      },
    ],
  },
  showcase: {
    title: 'Discover What Sora 2 Pro Can Create',
    description:
      'FalcoCut integrates Sora 2 Pro to transform simple prompts, ideas, or product photos into high-quality AI videos. Create cinematic scenes, product ads, and social media content without complex editing.',
    youtubeId: 'lEcg6AJ6DVY',
  },
  howTo: {
    title: 'How to Create Videos with Sora 2 Pro in FalcoCut',
    steps: [
      {
        title: 'Step 1: Choose Your Input',
        description:
          'Describe the video you want to create in Text to Video, or upload an image in Image to Video to animate your visuals.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/choose-your-input.png',
      },
      {
        title: 'Step 2: Select the Sora 2 Model',
        description:
          'Choose Sora 2 Pro inside FalcoCut to deliver realistic motion, detailed environments, and multi-shot storytelling for professional-grade results.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/choose-your-input.png',
      },
      {
        title: 'Step 3: Generate and Export',
        description:
          'Click Generate to create your AI video in seconds. Download or share your video instantly for social media or marketing use.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/generate-and-export.png',
      },
    ],
  },
  whyChoose: {
    title: 'Why Choose Sora AI Video Generator on FalcoCut',
    description:
      'Unlock limitless storytelling potential by using FalcoCut to harness the world-class physics and realism of Sora AI. This seamless integration allows you to turn complex ideas into breathtaking, high-definition videos with just a simple text description.',
    items: [
      {
        tag: 'Built for AI Video Creators',
        heading: 'Generate AI Videos\nfrom Text or Images',
        description:
          'Turn simple text prompts, images, or product photos into dynamic AI videos in seconds. Create marketing content, cinematic scenes, or storytelling clips without filming or editing.',
        bullets: [
          'Transform text prompts or images into ready-to-use videos',
          'Perfect for ads, product showcases, and creative storytelling',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/sora/generate-ai-videos-from-text-or-images.png',
      },
      {
        tag: 'Built for Storytelling Videos',
        heading: 'Create Smooth\nScene Transitions',
        description:
          'Generate multi-shot video sequences that flow naturally from scene to scene. FalcoCut ensures visual continuity, smooth transitions, and cinematic storytelling across every frame.',
        bullets: [
          'Generate multi-scene videos with natural transitions',
          'Maintain lighting and environment consistency across shots',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/sora/create-smooth-scene-transitions.png',
      },
      {
        tag: 'Built for Character-Driven Videos',
        heading: 'Maintain Character Consistency Across Scenes',
        description:
          'Keep characters, objects, and visual styles consistent throughout your video. FalcoCut ensures your protagonist looks identical across multiple shots for professional storytelling and ads.',
        bullets: [
          'Preserve character identity across scenes',
          'Maintain clothing, textures, and visual style automatically',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/sora/maintain-character-consistency-across-scenes.png',
      },
      {
        tag: 'Built for Social Media Creators',
        heading: 'Export Videos for Any Social Platform',
        description:
          'Optimize your videos for TikTok, Instagram Reels, and YouTube Shorts instantly. Adjust aspect ratios, duration, and format with one click to maximize publishing on social platforms.',
        bullets: [
          'Automatically format videos for social media platforms',
          'Generate content optimized for vertical video feeds',
        ],
        imageUrl:
          'https://assets.falcocut.ai/models/sora/export-videos-for-any-social-platform.png',
      },
    ],
  },
  whoCanUse: {
    title: 'Who Can Use Sora 2 Pro in FalcoCut',
    items: [
      {
        title: 'Content Creators',
        description:
          'Create engaging short-form videos, reels, and storytelling content using simple prompts or images. Generate unique visuals and trending content quickly using simple prompts or product images.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/content-creators.png',
      },
      {
        title: 'Marketing Teams & Brands',
        description:
          'Turn product photos or campaign ideas into high-quality AI video ads. Quickly generate creative assets for social media, product showcases, and branded campaigns at scale.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/marketing-teams-brands.png',
      },
      {
        title: 'Filmmakers & Creative Teams',
        description:
          'Prototype scenes, visualize story ideas, and experiment with cinematic shots using AI video generation. Quickly test concepts, pre-visualize narratives, and generate high-fidelity drafts.',
        imageUrl: 'https://assets.falcocut.ai/models/sora/filmmakers-creative-teams.png',
      },
    ],
  },
  socialProof: {
    youtube: {
      title: 'YouTube Videos about Sora 2',
      items: [
        { videoId: 'gzneGhpXwjU', url: 'https://www.youtube.com/watch?v=gzneGhpXwjU' },
        { videoId: 'lEcg6AJ6DVY', url: 'https://www.youtube.com/watch?v=lEcg6AJ6DVY' },
        { videoId: '1PaoWKvcJP0', url: 'https://www.youtube.com/watch?v=1PaoWKvcJP0' },
      ],
      fallbacks: [
        { videoId: 'QaiecWzeHFM', url: 'https://www.youtube.com/watch?v=QaiecWzeHFM' },
        { videoId: '0ly8PycvNjU', url: 'https://www.youtube.com/watch?v=0ly8PycvNjU' },
      ],
    },
    reddit: {
      title: 'Reddit Discussions about Sora 2',
      items: [
        {
          postUrl: 'https://www.reddit.com/r/OpenAI/comments/1nuj9d6/this_is_sora_2/',
          title: 'This is Sora 2.',
          subreddit: 'OpenAI',
        },
        {
          postUrl:
            'https://www.reddit.com/r/aivideo/comments/1nut0re/i_tested_sora_2_on_classic_anime_the_result_is/',
          title: 'I tested Sora 2 on classic anime, the result is hardly believable',
          author: 'u/Naegiko_',
          subreddit: 'aivideo',
        },
        {
          postUrl:
            'https://www.reddit.com/r/OpenAI/comments/1o3rfmk/my_very_first_sora_2_short_film/',
          title: 'My Very First Sora 2 Short Film',
          author: 'u/Theblasian35',
          subreddit: 'OpenAI',
        },
      ],
      fallbacks: [
        {
          postUrl:
            'https://www.reddit.com/r/OpenAI/comments/1oobyuv/ai_anime_episode_made_with_sora_2/',
          title: 'AI Anime Episode Made With Sora 2',
          author: 'u/No-Thing-9001',
          subreddit: 'OpenAI',
        },
        {
          postUrl:
            'https://www.reddit.com/r/aivideo/comments/1nv8bh1/i_tested_sora_20_with_the_most_complex_humain/',
          title: 'I tested Sora 2.0 with the most complex human motions',
          subreddit: 'aivideo',
        },
      ],
    },
    twitter: {
      title: 'Sora 2 on X',
      items: [
        { tweetId: '1973072672683684239' },
        { tweetId: '1866152437838393797' },
        { tweetId: '1973405124383985773' },
        { tweetId: '1973100138676756615' },
        { tweetId: '1973158674899280077' },
      ],
      fallbacks: [{ tweetId: '1973568567489798144' }, { tweetId: '1987602733277528300' }],
    },
  },
  exploreModels: {
    title: 'Explore More AI Models on FalcoCut',
    models: [
      { name: 'Seedance 2.0', href: '/models/seedance-2-0' },
      { name: 'Veo 3.1', href: '/models/veo-3-1' },
      { name: 'Kling V2.6 Pro', href: '/models/kling-v2-6-pro' },
      { name: 'Kling 2.5 Turbo Pro', href: '/models/kling-2-5-turbo-pro' },
      { name: 'Seedance V1.5 Pro', href: '/models/seedance-v1-5-pro' },
      { name: 'Nano Banana 2', href: '/models/nano-banana-2' },
      { name: 'Nano Banana Pro', href: '/models/nano-banana-pro' },
      { name: 'FLUX Kontext', href: '/models/flux-kontext' },
      {
        name: 'Kling V2.6 Pro Motion Control',
        href: '/models/kling-v2-6-pro-motion-control',
      },
      {
        name: 'Kling Video O1 Std Video Edit',
        href: '/models/kling-video-o1-std-video-edit',
      },
    ],
  },
  toolkits: {
    title: 'FalcoCut AI Toolkits',
    description:
      'FalcoCut provides a full suite of AI tools for creating high-quality videos faster. From AI video generation and avatars to translation and scalable content creation, everything you need to produce engaging video content in one platform.',
    items: [
      {
        title: 'AI Video Generator',
        href: '/ai-video-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-generator.png',
      },
      {
        title: 'AI Avatar Generator',
        href: '/ai-avatar-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-avatar-generator.png',
      },
      {
        title: 'Product Avatar',
        href: '/ai-product-avatar',
        imageUrl: 'https://assets.falcocut.ai/tools/common/product-avatar.png',
      },
      {
        title: 'AI Video Editor',
        href: '/ai-video-editor',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-editor.png',
      },
      {
        title: 'AI Video Translator',
        href: '/video-translator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-translator.png',
      },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What is Sora AI?',
        answer:
          '<p>Sora AI is a text‑to‑video model created by OpenAI that transforms written prompts or images into realistic moving scenes. It understands actions, environments, and physics well enough to generate videos that feel natural and coherent. The model can handle complex motion, multi‑shot storytelling, and consistent characters across frames. Its goal is to make high‑quality video creation accessible without requiring cameras, actors, or editing skills.</p>',
      },
      {
        question: 'How can I use Sora AI video generator on FalcoCut?',
        answer:
          '<ol><li><strong>Step 1:</strong> Select the Sora 2 Pro model inside FalcoCut to activate the latest text‑to‑video and image‑to‑video capabilities.</li><li><strong>Step 2:</strong> Enter a detailed prompt describing your idea or upload an image to guide the visual style and motion.</li><li><strong>Step 3:</strong> Choose your preferred duration and resolution so the final video matches your creative or platform needs.</li><li><strong>Step 4:</strong> Generate the video and download the finished clip once FalcoCut completes the rendering process.</li></ol>',
      },
      {
        question: 'What is the latest Sora AI model?',
        answer:
          '<p>The latest version of the Sora AI model is Sora 2 Pro, which offers improved realism, physics understanding, and scene consistency. It produces smoother motion, more accurate lighting, and better character stability across frames. Sora 2 Pro also supports synchronized audio, allowing sound effects and dialogue to match the generated visuals. This version represents a major leap in quality compared to earlier releases.</p>',
      },
      {
        question: 'Is Sora AI safe to use?',
        answer:
          '<p>Sora AI is designed with safety guidelines and content filters that help prevent harmful or inappropriate outputs. Platforms that integrate Sora, including FalcoCut, apply additional safeguards to ensure responsible use. These systems monitor prompts and generation behavior to maintain a safe creative environment. Users can create freely while knowing the tool follows established safety standards.</p>',
      },
      {
        question: 'Is Sora 2 Pro free to use?',
        answer:
          "<p>No. Access to Sora 2 Pro depends on the platform providing the model, and FalcoCut offers usage without requiring a separate OpenAI account. Many users can try Sora 2 Pro without upfront cost, especially for shorter or simpler generations. Some advanced features or higher‑resolution outputs may require a paid plan depending on the platform's policies. This structure allows creators to experiment before committing to premium options.</p>",
      },
      {
        question: 'What kind of videos can I create using Sora?',
        answer:
          '<p>Sora can generate a wide range of videos, including ads, product showcases, storytelling clips, educational scenes, and social media content. It handles both realistic environments and stylized concepts, giving creators flexibility across genres. The model supports multi‑shot sequences, allowing you to build short narratives or cinematic moments. Its versatility makes it suitable for marketing, entertainment, and creative experimentation.</p>',
      },
      {
        question: 'Can Sora 2 Pro handle image‑to‑video?',
        answer:
          '<p>Yes. Sora 2 Pro supports image‑to‑video generation by using your uploaded image as a visual reference for motion and style. The model can animate characters, environments, or objects while preserving the original look. This helps maintain consistency across scenes when you want a specific character or design. It is especially useful for product visuals, portraits, or concept art that needs dynamic movement.</p>',
      },
      {
        question: 'How long are the videos generated by Sora 2 Pro?',
        answer:
          '<p>Videos generated by Sora 2 Pro typically range from 1 to 2 minutes depending on the complexity of the prompt. More detailed scenes with multiple characters or dynamic motion may require additional processing time. Shorter clips usually render faster, especially when the prompt is simple and direct. The final duration is influenced by both creative choices and system capabilities.</p>',
      },
      {
        question: 'Can I edit videos generated with Sora 2 Pro?',
        answer:
          '<p>Yes. Videos generated with Sora 2 Pro can be edited further inside FalcoCut using its built‑in video editor. Users can regenerate the video by adjusting the prompt if they want a different interpretation or improved motion. The editor also allows trimming, adding text, inserting audio, or combining multiple clips. This flexibility helps refine the final output without needing external editing software.</p>',
      },
    ],
  },
  cta: {
    title: 'Try Sora 2 Pro Now',
    description: '',
    buttonText: 'Try Sora 2 Pro Now',
    imageUrl: 'https://assets.falcocut.ai/models/sora/try-sora-2-pro-on-falcocut.png',
  },
  bottomCta: {
    title: 'Start Creating Videos with Sora 2',
    description:
      'Use Sora 2 in FalcoCut to generate high-quality AI videos from text prompts or images. Create cinematic scenes, marketing content, and social media videos in minutes.',
    buttonText: 'Try Sora 2 Now',
  },
  seo: {
    title: 'Sora 2 Pro AI Video Generator - Create Stunning Videos | FalcoCut',
    description:
      'Generate high-quality AI videos with Sora 2 Pro on FalcoCut. Transform text and images into cinematic videos with realistic physics simulation. Try free today.',
    keywords:
      'Sora 2 Pro, AI video generator, text to video, OpenAI Sora, AI video creation, FalcoCut',
    publishedTime: '2026-03-11',
    modifiedTime: '2026-03-11',
  },
};

export default config;
